<template>
    <div class="dashboard-container">
        <side-bar />
        <div class="main-content">
            <div class="container">
                <partner-breadcrumbs />
            </div>

            <!-- Tab Navigation -->
            <div class="tabs">
                <div class="tab-header">
                    <button v-if="!isMedicalBot" class="tab-button" :class="{ active: activeTab === 'conv-history' }"
                        @click="$router.push('/partner/conversation-history/voice')">
                        Conversation History
                    </button>
                    <button class="tab-button" :class="{ active: activeTab === 'bot-analytics' }"
                        @click="$router.push('/partner/analytics')">
                        Analytics
                    </button>
                </div>

                <div class="tab-content">

                    <!-- ASPIRE BOT TAB -->
                    <div v-if="isAspireBot">

                        <!-- Date Range Filter -->
                        <v-container>
                            <v-row justify="start" align="center">
                                <v-col sm="auto">
                                    <span class="text-subtitle-1" style="font-weight: bold;">Date range:</span>
                                </v-col>
                                <v-col sm="auto">
                                    <v-radio-group v-model="aspireRange" inline hide-details>
                                        <v-radio label="Last 7 days" value="7" />
                                        <v-radio label="Last 14 days" value="14" />
                                        <v-radio label="Custom" value="Custom" />
                                    </v-radio-group>
                                </v-col>
                                <v-col sm="auto" v-if="aspireRange === 'Custom'">
                                    <v-text-field v-model="aspireStartDate" label="Start Date" type="date"
                                        density="compact" variant="outlined" clearable />
                                </v-col>
                                <v-col sm="auto" v-if="aspireRange === 'Custom'">
                                    <v-text-field v-model="aspireEndDate" label="End Date" type="date" density="compact"
                                        variant="outlined" clearable />
                                </v-col>
                                <v-col sm="auto">
                                    <v-btn color="blue-grey-darken-2" class="text-none px-6 mt-n3" height="40"
                                        rounded="xl" @click="loadAspireAnalytics">
                                        SUBMIT
                                    </v-btn>
                                </v-col>
                            </v-row>
                        </v-container>

                        <!-- Analytics Tiles + Location Table -->
                        <v-container fluid class="analytics-container mb-6">

                            <!-- KPI Tiles -->
                            <v-row class="mb-6">
                                <v-col cols="12">
                                    <v-card class="pa-4">
                                        <h3 class="section-title mb-5">Overall Statistics</h3>
                                        <v-row dense>
                                            <v-col cols="6" sm="3" class="mb-3">
                                                <div class="kpi-card kpi-blue">
                                                    <div class="kpi-header">
                                                        <v-icon size="26" class="kpi-icon">mdi-phone</v-icon>
                                                        <div class="kpi-label">Total Calls</div>
                                                    </div>
                                                    <div class="kpi-value">{{ aspireStats.Calls }}</div>
                                                </div>
                                            </v-col>
                                            <v-col cols="6" sm="3" class="mb-3">
                                                <div class="kpi-card kpi-amber">
                                                    <div class="kpi-header">
                                                        <v-icon size="26" class="kpi-icon">mdi-swap-horizontal</v-icon>
                                                        <div class="kpi-label">Transfers</div>
                                                    </div>
                                                    <div class="kpi-value">{{ aspireStats.Transfers }}</div>
                                                </div>
                                            </v-col>
                                            <v-col cols="6" sm="3" class="mb-3">
                                                <div class="kpi-card kpi-teal">
                                                    <div class="kpi-header">
                                                        <v-icon size="26" class="kpi-icon">mdi-email-outline</v-icon>
                                                        <div class="kpi-label">Emails</div>
                                                    </div>
                                                    <div class="kpi-value">{{ aspireStats.Emails }}</div>
                                                </div>
                                            </v-col>
                                            <v-col cols="6" sm="3" class="mb-3">
                                                <div class="kpi-card kpi-green">
                                                    <div class="kpi-header">
                                                        <v-icon size="26"
                                                            class="kpi-icon">mdi-message-text-outline</v-icon>
                                                        <div class="kpi-label">SMSs</div>
                                                    </div>
                                                    <div class="kpi-value">{{ aspireStats.SMSs }}</div>
                                                </div>
                                            </v-col>
                                        </v-row>
                                    </v-card>
                                </v-col>
                            </v-row>

                            <!-- Call Transfers By Location Table -->
                            <v-row>
                                <v-col cols="12">
                                    <v-card class="pa-4">
                                        <h3 class="section-title mb-4">Call Transfers By Location</h3>
                                        <v-data-table :headers="aspireLocationHeaders" :items="aspireLocationData"
                                            density="compact" :items-per-page="15" />
                                    </v-card>
                                </v-col>
                            </v-row>

                        </v-container>
                    </div>

                    <!-- STANDARD (NON-MEDICAL) BOT TAB -->
                    <div v-else-if="activeTab === 'bot-analytics' && !isMedicalBot">

                        <!-- Date Range Filter -->
                        <v-container>
                            <v-row justify="start" align="center">
                                <v-col sm="auto">
                                    <span class="text-subtitle-1" style="font-weight: bold;">Date range:</span>
                                </v-col>
                                <v-col sm="auto">
                                    <v-radio-group v-model="standardRange" inline hide-details>
                                        <v-radio label="Last 7 days" value="7" />
                                        <v-radio label="Last 14 days" value="14" />
                                        <v-radio label="Custom" value="Custom" />
                                    </v-radio-group>
                                </v-col>
                                <v-col sm="auto" v-if="standardRange === 'Custom'">
                                    <v-text-field v-model="standardStartDate" label="Start Date" type="date"
                                        density="compact" variant="outlined" clearable />
                                </v-col>
                                <v-col sm="auto" v-if="standardRange === 'Custom'">
                                    <v-text-field v-model="standardEndDate" label="End Date" type="date"
                                        density="compact" variant="outlined" clearable />
                                </v-col>
                                <v-col sm="auto">
                                    <v-btn color="blue-grey-darken-2" class="text-none px-6 mt-n3" height="40"
                                        rounded="xl" @click="loadStandardAnalytics">
                                        SUBMIT
                                    </v-btn>
                                </v-col>
                            </v-row>
                        </v-container>

                        <!-- Chart + Overall Stats -->
                        <v-container fluid class="analytics-container mb-6">
                            <v-row class="analytics-wrapper" align="stretch">

                                <!-- Bar Chart -->
                                <v-col cols="12" md="8" class="d-flex">
                                    <v-card class="pa-3 h-100 w-100">
                                        <h3 class="section-title mb-4">{{ isPPBot ? 'Weekly Total Calls and Transfers to PM' : isFleetBot ? 'Weekly Total Calls, Transfers and Availability Logged' : 'Weekly Total Calls, Transfers and SMSs' }}</h3>
                                        <div class="chart-wrapper">
                                            <canvas ref="standardBarChart"></canvas>
                                        </div>
                                    </v-card>
                                </v-col>

                                <!-- Overall Statistics Cards — driven by computed tiles -->
                                <v-col cols="12" md="4" class="d-flex">
                                    <v-card class="pa-4 h-100 w-100">
                                        <h3 class="section-title mb-5">Overall Statistics</h3>
                                        <v-row dense>
                                            <v-col v-for="tile in computedStandardTiles" :key="tile.key" cols="6"
                                                class="mb-3">
                                                <div :class="['kpi-card', tile.colorClass]">
                                                    <div class="kpi-header">
                                                        <v-icon size="26" class="kpi-icon">{{ tile.icon }}</v-icon>
                                                        <div class="kpi-label">{{ tile.label }}</div>
                                                    </div>
                                                    <div class="kpi-value">{{ formatStandardTileValue(tile) }}</div>
                                                </div>
                                            </v-col>
                                        </v-row>
                                    </v-card>
                                </v-col>
                            </v-row>

                            <!-- Download Button -->
                            <v-row justify="end" class="mt-4">
                                <v-col cols="auto">
                                    <v-btn color="primary" class="text-none" rounded="xl" prepend-icon="mdi-download"
                                        @click="downloadStandardSummary">
                                        Download Summary
                                    </v-btn>
                                </v-col>
                            </v-row>

                            <!-- Weekly Summary Table — driven by computed headers -->
                            <v-row class="mt-2">
                                <v-col cols="12">
                                    <v-card class="pa-4 mb-4">
                                        <h3 class="section-title mb-4">Weekly Summary</h3>
                                        <v-data-table :headers="computedStandardWeeklyHeaders"
                                            :items="standardWeeklyData" hide-default-footer :items-per-page="-1"
                                            density="compact">
                                            <template v-slot:item.week="{ item }">
                                                <span class="font-weight-bold pr-1">
                                                    {{ item.week.substring(0, item.week.indexOf('(')) }}
                                                </span>
                                                <span>{{ item.week.substring(item.week.indexOf('(')) }}</span>
                                            </template>
                                        </v-data-table>
                                    </v-card>
                                </v-col>
                            </v-row>

                            <!-- Daily Summary Table — driven by computed headers -->
                            <v-row>
                                <v-col cols="12">
                                    <v-card class="pa-4">
                                        <h3 class="section-title mb-4">Daily Summary</h3>
                                        <v-data-table :headers="computedStandardDailyHeaders" :items="standardDailyData"
                                            density="compact" />
                                    </v-card>
                                </v-col>
                            </v-row>
                        </v-container>
                    </div>

                    <!--  MEDICAL BOT TAB -->
                    <div v-else-if="isMedicalBot" class="Medicalbotcontainer">

                        <!-- Medical Analytics: Date Range Selector -->
                        <v-container>
                            <v-row justify="start">
                                <v-col sm="auto">
                                    <span class="text-subtitle-1" style="font-weight: bold;">Date range:</span>
                                </v-col>
                                <v-col sm="auto">
                                    <v-radio-group v-model="medicalRange" inline hide-details>
                                        <v-radio label="Last 7 days" value="7" />
                                        <v-radio label="Last 14 days" value="14" />
                                        <v-radio label="Custom" value="Custom" />
                                    </v-radio-group>
                                </v-col>

                                <v-col sm="auto" v-if="medicalRange === 'Custom'">
                                    <v-text-field v-model="medicalStartDate" label="Start Date" type="date"
                                        density="compact" variant="outlined" clearable />
                                </v-col>
                                <v-col sm="auto" v-if="medicalRange === 'Custom'">
                                    <v-text-field v-model="medicalEndDate" label="End Date" type="date"
                                        density="compact" variant="outlined" clearable />
                                </v-col>

                                <v-col sm="auto">
                                    <v-btn color="blue-grey-darken-2" class="text-none px-6 mt-n3" height="40"
                                        rounded="xl" @click="loadMedicalAnalytics">
                                        SUBMIT
                                    </v-btn>
                                </v-col>
                            </v-row>
                        </v-container>

                        <!-- Analytics Section -->
                        <v-container fluid class="analytics-container mb-6">
                            <v-row class="analytics-wrapper" align="stretch">

                                <!-- Main Chart: Performance over time -->
                                <v-col cols="12" md="8" class="d-flex">
                                    <v-card class="pa-3 h-100 w-100">
                                        <h3 class="section-title mb-4">Weekly Total Calls, Handled Calls and Bookings
                                        </h3>
                                        <div class="chart-wrapper">
                                            <canvas ref="barChart"></canvas>
                                        </div>
                                        <!-- <div class="chart-note">
                                            * These stats are available from 23rd or 24th May onwards
                                        </div> -->
                                    </v-card>
                                </v-col>

                                <!-- Overall KPI Statistics for medical bots -->
                                <v-col cols="12" md="4" class="d-flex">
                                    <v-card class="pa-4 h-100 w-100">
                                        <h3 class="section-title mb-5">Overall Statistics</h3>
                                        <v-row dense class="mb-0">
                                            <v-col cols="12" md="6" class="mb-3">
                                                <div class="kpi-card kpi-blue">
                                                    <div class="kpi-header">
                                                        <v-icon size="26" class="kpi-icon">mdi-phone</v-icon>
                                                        <div class="kpi-label">Total Calls</div>
                                                    </div>
                                                    <div class="kpi-value">{{ medicalStats.totalCalls }}</div>
                                                </div>
                                            </v-col>
                                            <v-col cols="12" md="6" class="mb-3">
                                                <div class="kpi-card kpi-amber">
                                                    <div class="kpi-header">
                                                        <v-icon size="26"
                                                            class="kpi-icon">mdi-chart-line-variant</v-icon>
                                                        <div class="kpi-label">% of Calls Handled</div>
                                                    </div>
                                                    <div class="kpi-value">{{ medicalStats.handledRate }}%</div>
                                                </div>
                                            </v-col>
                                            <v-col cols="12" md="6" class="mb-3">
                                                <div class="kpi-card kpi-teal">
                                                    <div class="kpi-header">
                                                        <v-icon size="26" class="kpi-icon">mdi-chart-pie</v-icon>
                                                        <div class="kpi-label">% of Bookings</div>
                                                    </div>
                                                    <div class="kpi-value">{{ medicalStats.bookingRate }}%</div>
                                                </div>
                                            </v-col>
                                            <v-col cols="12" md="6" class="mb-3">
                                                <div class="kpi-card kpi-green">
                                                    <div class="kpi-header">
                                                        <v-icon size="26" class="kpi-icon">mdi-check-circle</v-icon>
                                                        <div class="kpi-label-with-tooltip">
                                                            Actions Completed
                                                            <v-tooltip location="top" max-width="280">
                                                                <template #activator="{ props }">
                                                                    <v-icon size="16" class="info-icon" v-bind="props">mdi-information</v-icon>
                                                                </template>
                                                                Actions Completed Percentage is the percentage of all bookings, cancellations, reschedules, and appointment confirmations completed by the agent.
                                                            </v-tooltip>
                                                        </div>
                                                    </div>
                                                    <div class="kpi-value">{{ medicalStats.actionsCompletedRate }}%</div>
                                                </div>
                                            </v-col>
                                        </v-row>
                                    </v-card>
                                </v-col>
                            </v-row>

                            <!-- Handled Calls Details -->
                            <v-card class="pa-4 mb-4 mt-4">
                                <div class="d-flex align-center gap-3 mt-2 mb-3">
                                    <h3 class="section-title">Handled Calls Details</h3>
                                </div>

                                <v-row dense class="mb-3">
                                    <v-col>
                                        <div class="new-detail-card detail-green h-100">
                                            <div class="new-detail-top">
                                                <v-icon size="20" class="detail-icon">mdi-calendar-check</v-icon>
                                                <span class="new-detail-label">Bookings</span>
                                            </div>
                                            <div class="new-detail-value">
                                                {{ medicalStats.bookings }}
                                                <span class="new-detail-rate-badge">{{ medicalStats.bookingRate
                                                    }}%</span>
                                            </div>
                                        </div>
                                    </v-col>
                                    <v-col>
                                        <div class="new-detail-card detail-teal h-100">
                                            <div class="new-detail-top">
                                                <v-icon size="20" class="detail-icon">mdi-calendar-refresh</v-icon>
                                                <span class="new-detail-label">Reschedules</span>
                                            </div>
                                            <div class="new-detail-value">
                                                {{ medicalStats.reschedules }}
                                                <span class="new-detail-rate-badge">{{
                                                    medicalStats.reschedulesRate }}%</span>
                                            </div>
                                        </div>
                                    </v-col>
                                    <v-col>
                                        <div class="new-detail-card detail-orange h-100">
                                            <div class="new-detail-top">
                                                <v-icon size="20" class="detail-icon">mdi-calendar-question</v-icon>
                                                <span class="new-detail-label">Check Appts</span>
                                            </div>
                                            <div class="new-detail-value">
                                                {{ medicalStats.confirmBooking }}
                                                <span class="new-detail-rate-badge">{{ medicalStats.confirmBookingRate
                                                }}%</span>
                                            </div>
                                        </div>
                                    </v-col>
                                    <v-col>
                                        <div class="new-detail-card detail-yellow h-100">
                                            <div class="new-detail-top">
                                                <v-icon size="20" class="detail-icon">mdi-calendar-search</v-icon>
                                                <span class="new-detail-label">Check Availability</span>
                                            </div>
                                            <div class="new-detail-value">
                                                {{ medicalStats.checkAvailability }}
                                                <span class="new-detail-rate-badge">{{
                                                    medicalStats.checkAvailabilityRate }}%</span>
                                            </div>
                                        </div>
                                    </v-col>
                                    <v-col>
                                        <div class="new-detail-card detail-red h-100">
                                            <div class="new-detail-top">
                                                <v-icon size="20" class="detail-icon">mdi-cancel</v-icon>
                                                <span class="new-detail-label">Cancellations</span>
                                            </div>
                                            <div class="new-detail-value">
                                                {{ medicalStats.cancellations }}
                                                <span class="new-detail-rate-badge">{{
                                                    medicalStats.cancellationsRate }}%</span>
                                            </div>
                                        </div>
                                    </v-col>
                                </v-row>
                            </v-card>

                            <!-- AI Analysis Cards -->
                            <!-- <v-card class="pa-4 mb-4">
                                <h3 class="section-title mb-4">AI Supervisor Analysis</h3>
                                <div class="ai-cards-grid mb-6">
                                    <v-card v-for="(card, index) in analyticsCards" :key="index" elevation="0"
                                        class="ai-analytics-card">
                                        <div class="ai-card-header" :style="{ background: card.headerColor }">
                                            <div class="ai-card-title">{{ card.title }}</div>
                                            <div class="ai-card-subtitle">{{ card.subtitle }}</div>
                                        </div>
                                        <div class="ai-card-body">
                                            <table class="ai-table">
                                                <thead>
                                                    <tr>
                                                        <th class="ai-th">{{ index >= 2 ? 'Score' : 'Category' }}</th>
                                                        <th class="ai-th ai-th-center">No.</th>
                                                        <th class="ai-th ai-th-center">%</th>
                                                    </tr>
                                                </thead>
                                                <tbody>
                                                    <tr v-for="(row, rowIndex) in card.rows" :key="rowIndex"
                                                        class="ai-row">
                                                        <td class="ai-td">{{ row.category || '—' }}</td>
                                                        <td class="ai-td ai-td-center">
                                                            <span
                                                                v-if="row.number !== '' && row.number !== null && row.number !== undefined"
                                                                class="ai-num-pill"
                                                                :style="{ background: card.headerColor + '18', color: card.headerColor }">
                                                                {{ row.number }}
                                                            </span>
                                                            <span v-else class="ai-dash">—</span>
                                                        </td>
                                                        <td class="ai-td ai-td-center">
                                                            <div v-if="row.percentage !== '' && row.percentage !== null && row.percentage !== undefined"
                                                                class="ai-pct-wrap">
                                                                <div class="ai-pct-bar">
                                                                    <div class="ai-pct-fill" :style="{
                                                                        width: parseFloat(row.percentage) + '%',
                                                                        background: card.headerColor
                                                                    }"></div>
                                                                </div>
                                                                <span class="ai-pct-text"
                                                                    :style="{ color: card.headerColor }">
                                                                    {{ row.percentage }}%
                                                                </span>
                                                            </div>
                                                            <span v-else class="ai-dash">—</span>
                                                        </td>
                                                    </tr>
                                                </tbody>
                                            </table>
                                        </div>
                                    </v-card>
                                </div>
                            </v-card> -->

                            <!-- Download Button -->
                            <v-row justify="end">
                                <v-col cols="auto">
                                    <v-btn color="primary" class="text-none" rounded="xl" prepend-icon="mdi-download"
                                        @click="downloadSummary">
                                        Download Summary
                                    </v-btn>
                                </v-col>
                            </v-row>

                            <!-- Weekly Summary Table -->
                            <v-row>
                                <v-col cols="12">
                                    <v-card class="pa-4 mb-4">
                                        <div class="d-flex justify-space-between align-center mb-4">
                                            <h3 class="section-title">Weekly Summary</h3>
                                        </div>
                                        <v-data-table :headers="weeklyHeaders" :items="weeklyData" hide-default-footer
                                            :items-per-page="-1" density="compact">
                                            <template v-slot:item.week="{ item }">
                                                <span class="font-weight-bold pr-1">
                                                    {{ item.week.substring(0, item.week.indexOf('(')) }}
                                                </span>
                                                <span>{{ item.week.substring(item.week.indexOf('(')) }}</span>
                                            </template>
                                        </v-data-table>
                                    </v-card>
                                </v-col>
                            </v-row>

                            <!-- Daily Summary Table -->
                            <v-row>
                                <v-col cols="12">
                                    <v-card class="pa-4">
                                        <div class="d-flex justify-space-between align-center mb-4">
                                            <h3 class="section-title">Daily Summary</h3>
                                        </div>
                                        <v-data-table :headers="headers" :items="tableData" density="compact" />
                                    </v-card>
                                </v-col>
                            </v-row>
                        </v-container>
                    </div>

                </div>
            </div>
        </div>
    </div>
</template>

<script>
import SideBar from './SideBar.vue'
import Chart from 'chart.js/auto'
import * as XLSX from 'xlsx'
import PartnerBreadcrumbs from './PartnerBreadcrumbs.vue'
import { Chart as ChartJS, Title, Tooltip, Legend, ArcElement } from 'chart.js'
import { medicalBotIds } from '@/config/medicalBots.js'

ChartJS.register(Title, Tooltip, Legend, ArcElement)

//  Bot ID Constants 
const ASPIRE_BOT_ID = '89773371'

/**
 * Bot ID for the property-focused standard bot that shows extended metrics:
 * Total Calls, Transfers, SMSs, Prop Details, REA Listing, Email Action.
 * Emails tile/column is hidden for this bot.
 */
const PROPERTY_BOT_ID = '18457558'

const FLEET_BOT_IDS = ['24513573', '58269475']

/**
 * Bot ID for Burwood Bot — hides the update notice banner.
 */
const BURWOOD_BOT_ID = '20012341'

//  All possible standard tile definitions 
const ALL_STANDARD_TILES = [
    { key: 'totalCalls', label: 'Total Calls', icon: 'mdi-phone', colorClass: 'kpi-blue' },
    { key: 'transfers', label: 'Transfers', icon: 'mdi-swap-horizontal', colorClass: 'kpi-amber' },
    { key: 'emails', label: 'Emails', icon: 'mdi-email-outline', colorClass: 'kpi-teal' },
    { key: 'smss', label: 'SMSs', icon: 'mdi-message-text-outline', colorClass: 'kpi-green' },
    { key: 'availabilityLogged', label: 'Availability Logged', icon: 'mdi-calendar-check', colorClass: 'kpi-green' },
    { key: 'propertyDetails', label: 'Properties Fetched', icon: 'mdi-home-search', colorClass: 'kpi-blue' },
    { key: 'reaListing', label: 'REA Listings Fetched', icon: 'mdi-format-list-bulleted', colorClass: 'kpi-amber' },
    { key: 'sendEmailAction', label: 'Emails Sent', icon: 'mdi-email-arrow-right-outline', colorClass: 'kpi-teal' },
]

//  All possible weekly header definitions 
const ALL_STANDARD_WEEKLY_HEADERS = [
    { title: 'Week', key: 'week' },
    { title: 'Total Calls', key: 'totalCalls' },
    { title: 'Transfers', key: 'transfers' },
    { title: 'Emails', key: 'emails' },
    { title: 'SMSs', key: 'smss' },
    { title: 'Availability Logged', key: 'availabilityLogged' },
    { title: 'Properties Fetched', key: 'propertyDetails' },
    { title: 'REA Listings Fetched', key: 'reaListing' },
    { title: 'Emails Sent', key: 'sendEmailAction' },
]

//  All possible daily header definitions 
const ALL_STANDARD_DAILY_HEADERS = [
    { title: 'Date', key: 'Date' },
    { title: 'Total Calls', key: 'Total_Calls' },
    { title: 'Transfers', key: 'Transfers' },
    { title: 'Emails', key: 'Emails' },
    { title: 'SMSs', key: 'SMSs' },
    { title: 'Availability Logged', key: 'AvailabilityLogged' },
    { title: 'Properties Fetched', key: 'PropertyDetails' },
    { title: 'REA Listings Fetched', key: 'ReaListing' },
    { title: 'Emails Sent', key: 'SendEmailAction' },
]

//  Keys visible per bot type 
/**
 * Property bot (18457558): show all except Emails.
 * All other standard bots: show only the 4 base metrics.
 */
const PROPERTY_BOT_TILE_KEYS = ['totalCalls', 'transfers', 'smss', 'propertyDetails', 'reaListing', 'sendEmailAction']
const PROPERTY_BOT_WEEKLY_KEYS = ['week', 'totalCalls', 'transfers', 'propertyDetails', 'reaListing', 'sendEmailAction']
const PROPERTY_BOT_DAILY_KEYS = ['Date', 'Total_Calls', 'Transfers', 'PropertyDetails', 'ReaListing', 'SendEmailAction']

const FLEET_BOT_TILE_KEYS = ['totalCalls', 'transfers', 'availabilityLogged']
const FLEET_BOT_WEEKLY_KEYS = ['week', 'totalCalls', 'transfers', 'availabilityLogged']
const FLEET_BOT_DAILY_KEYS = ['Date', 'Total_Calls', 'Transfers', 'AvailabilityLogged']

const DEFAULT_BOT_TILE_KEYS = ['totalCalls', 'transfers', 'emails', 'smss']
const DEFAULT_BOT_WEEKLY_KEYS = ['week', 'totalCalls', 'transfers', 'emails', 'smss']
const DEFAULT_BOT_DAILY_KEYS = ['Date', 'Total_Calls', 'Transfers', 'Emails', 'SMSs']

export default {
    name: 'BotAnalytics',

    components: {
        SideBar,
        PartnerBreadcrumbs,
    },

    data() {
        return {
            //  Bot ID Constants (accessible to template)
            BURWOOD_BOT_ID: '20012341',

            //  General State 
            activeTab: 'bot-analytics',
            isMedicalBot: false,
            isAspireBot: false,

            /**
             * Stores the raw botId string so computed properties can branch
             * without calling localStorage on every render.
             */
            currentBotId: '',

            //  Aspire Bot State 
            aspireRange: '7',
            aspireStartDate: null,
            aspireEndDate: null,

            aspireStats: {
                Calls: 0,
                Transfers: 0,
                Emails: 0,
                SMSs: 0,
            },

            aspireLocationHeaders: [
                { title: 'Location', key: 'Location' },
                { title: 'Calls', key: 'Calls' },
            ],
            aspireLocationData: [],

            //  Standard (Non-Medical) Bot State 
            standardRange: '7',
            standardStartDate: null,
            standardEndDate: null,

            standardStats: {
                totalCalls: 0,
                transfers: 0,
                emails: 0,
                smss: 0,
                propertyDetails: 0,
                reaListing: 0,
                sendEmailAction: 0,
                availabilityLogged: 0,
            },

            // NOTE: standardWeeklyHeaders, standardDailyHeaders are intentionally
            // removed from data() — they are now driven by computed properties
            // (computedStandardWeeklyHeaders / computedStandardDailyHeaders)
            // so the base config is never mutated.
            standardWeeklyData: [],
            standardDailyData: [],
            standardChartInstance: null,

            //  Medical Bot State 
            medicalRange: '7',
            medicalStartDate: null,
            medicalEndDate: null,

            medicalStats: {
                totalCalls: 0,
                bookings: 0,
                handledCalls: 0,
                bookingRate: 0,
                handledRate: 0,
                checkAvailability: 0,
                checkAvailabilityRate: 0,
                cancellations: 0,
                cancellationsRate: 0,
                reschedules: 0,
                reschedulesRate: 0,
                confirmBooking: 0,
                confirmBookingRate: 0,
                actionsCompletedRate: 0,
            },

            chartInstance: null,

            // ── Medical weekly rows — MUST be declared here for Vue reactivity ──
            weeklyData: [],

            weeklyHeaders: [
                { title: 'Week', key: 'week' },
                { title: 'Total Calls', key: 'totalCalls' },
                { title: 'Handled Calls', key: 'handledCalls' },
                { title: 'Bookings', key: 'bookings' },
                { title: 'Check Availability', key: 'checkAvailability' },
                { title: 'Cancellations', key: 'cancellations' },
                { title: 'Reschedules', key: 'reschedules' },
                { title: 'Confirm Booking', key: 'confirmBooking' },
            ],
            headers: [
                { title: 'Date', key: 'Date' },
                { title: 'Total Calls', key: 'Total_Calls' },
                { title: 'Handled Calls', key: 'Handled_Calls' },
                { title: 'Bookings', key: 'Bookings' },
                { title: 'Check Availability', key: 'Check_Availability' },
                { title: 'Cancellations', key: 'Cancellations' },
                { title: 'Reschedules', key: 'Reschedules' },
                { title: 'Confirm Booking', key: 'Confirm_Booking' },
            ],
            tableData: [],

            analyticsCards: [
                {
                    title: 'OUTCOME CATEGORY',
                    headerColor: '#2196F3',
                    rows: [
                        { category: 'Booking Confirmed', number: 51, percentage: 32 },
                        { category: 'Call Transferred', number: 38, percentage: 24 },
                        { category: 'Info Provided', number: 45, percentage: 28 },
                        { category: 'Unresolved', number: 27, percentage: 16 },
                    ],
                },
                {
                    title: 'REASON CATEGORY',
                    headerColor: '#FF9800',
                    rows: [
                        { category: 'Appointment Booking', number: 63, percentage: 39 },
                        { category: 'Cancellation', number: 29, percentage: 18 },
                        { category: 'General Inquiry', number: 42, percentage: 26 },
                        { category: 'Reschedule', number: 27, percentage: 17 },
                    ],
                },
                {
                    title: 'CSAT SCORE',
                    headerColor: '#7C3AED',
                    rows: [
                        { category: '5 - Excellent', number: 74, percentage: 46 },
                        { category: '4 - Good', number: 48, percentage: 30 },
                        { category: '3 - Neutral', number: 26, percentage: 16 },
                        { category: '1-2 - Poor', number: 13, percentage: 8 },
                    ],
                },
                {
                    title: 'AI SUPERVISOR SCORE',
                    headerColor: '#4CAF50',
                    rows: [
                        { category: '90-100 (Excellent)', number: 58, percentage: 36 },
                        { category: '70-89 (Good)', number: 53, percentage: 33 },
                        { category: '50-69 (Average)', number: 35, percentage: 22 },
                        { category: 'Below 50 (Poor)', number: 15, percentage: 9 },
                    ],
                },
            ],
        }
    },

    //  Computed Properties 
    computed: {
        /**
         * Returns true only when the currently loaded bot is the property bot
         * (ID 18457558). Used exclusively by standard-analytics computed props.
         * Has no effect on medical, Aspire, or any other bot type.
         */
        isPPBot() {
            return this.currentBotId === PROPERTY_BOT_ID
        },

        isFleetBot() {
            return FLEET_BOT_IDS.includes(this.currentBotId)
        },

        /**
         * KPI tiles rendered inside the Overall Statistics card for standard bots.
         * Property bot → 6 tiles (no Emails).
         * All other standard bots → 4 tiles (base metrics only).
         */
        computedStandardTiles() {
            const allowedKeys = this.isFleetBot
                ? FLEET_BOT_TILE_KEYS
                : this.isPPBot
                    ? PROPERTY_BOT_TILE_KEYS
                    : DEFAULT_BOT_TILE_KEYS

            const tiles = ALL_STANDARD_TILES.filter(t => allowedKeys.includes(t.key))
                .map(t => {
                    if (this.isPPBot && t.key === 'smss') {
                        return {
                            ...t,
                            label: 'Transfers %',
                            icon: 'mdi-percent',
                        }
                    }
                    if (this.isPPBot && t.key === 'transfers') {
                        return {
                            ...t,
                            label: 'Transfers to PM',
                        }
                    }
                    return t
                })

            return tiles
        },

        /**
         * Weekly summary table headers for standard bots.
         * Filtered by the same key sets as the tiles.
         */
        computedStandardWeeklyHeaders() {
            const allowedKeys = this.isFleetBot
                ? FLEET_BOT_WEEKLY_KEYS
                : this.isPPBot
                    ? PROPERTY_BOT_WEEKLY_KEYS
                    : DEFAULT_BOT_WEEKLY_KEYS

            const headers = ALL_STANDARD_WEEKLY_HEADERS.filter(h => allowedKeys.includes(h.key))
                .map(h => {
                    if (this.isPPBot && h.key === 'smss') {
                        return { ...h, title: 'Transfers %' }
                    }
                    if (this.isPPBot && h.key === 'transfers') {
                        return { ...h, title: 'Transfers to PM' }
                    }
                    return h
                })

            return headers
        },

        /**
         * Daily summary table headers for standard bots.
         * Filtered by the same key sets as the tiles.
         */
        computedStandardDailyHeaders() {
            const allowedKeys = this.isFleetBot
                ? FLEET_BOT_DAILY_KEYS
                : this.isPPBot
                    ? PROPERTY_BOT_DAILY_KEYS
                    : DEFAULT_BOT_DAILY_KEYS

            const headers = ALL_STANDARD_DAILY_HEADERS.filter(h => allowedKeys.includes(h.key))
                .map(h => {
                    if (this.isPPBot && h.key === 'SMSs') {
                        return { ...h, title: 'Transfers %' }
                    }
                    if (this.isPPBot && h.key === 'Transfers') {
                        return { ...h, title: 'Transfers to PM' }
                    }
                    return h
                })

            return headers
        },
    },

    //  Lifecycle 
    async mounted() {
        if (!localStorage.getItem('customerGUID')) {
            this.$router.push('/partner/dashboard')
            return
        }

        const botId = localStorage.getItem('botID') || ''
        this.currentBotId = botId
        const botType = botId === ASPIRE_BOT_ID
            ? 'Aspire'
            : medicalBotIds.includes(botId)
                ? 'Medical'
                : botId === PROPERTY_BOT_ID
                    ? 'Property (standard extended)'
                    : 'Standard (base)'


        if (!botId) {
            this.$router.push('/partner/customer')
            return
        }

        if (botId === ASPIRE_BOT_ID) {
            this.isAspireBot = true
            await this.loadAspireAnalytics()
        } else if (medicalBotIds.includes(botId)) {
            this.isMedicalBot = true
            await this.loadMedicalAnalytics()
        } else {
            await this.loadStandardAnalytics()
        }
    },

    //  Methods 
    methods: {

        //  ASPIRE BOT METHODS 

        /**
         * Builds start/end dates from aspireRange selection.
         */
        _getAspireDateRange() {
            const today = new Date()
            today.setHours(23, 59, 59, 999)
            let start, end

            if (this.aspireRange === '7') {
                end = today.toISOString().split('T')[0]
                const s = new Date(today)
                s.setDate(s.getDate() - 7)
                start = s.toISOString().split('T')[0]
            } else if (this.aspireRange === '14') {
                end = today.toISOString().split('T')[0]
                const s = new Date(today)
                s.setDate(s.getDate() - 14)
                start = s.toISOString().split('T')[0]
            } else if (this.aspireRange === 'Custom' && this.aspireStartDate && this.aspireEndDate) {
                start = this.aspireStartDate
                const endDate = new Date(this.aspireEndDate)
                endDate.setDate(endDate.getDate() - 1)
                end = endDate.toISOString().split('T')[0]
            }

            return { start, end }
        },

        _calculateTransferPercent(totalCalls, transferCount) {
            if (!totalCalls) return 0
            return Number(((transferCount / totalCalls) * 100).toFixed(1))
        },

        formatStandardTileValue(tile) {
            if (this.isPPBot && tile.key === 'smss') {
                return `${this.standardStats.smss}%`
            }
            return this.standardStats[tile.key]
        },

        /**
         * Fetches and maps Aspire bot analytics from the dedicated endpoint.
         * Populates aspireStats (KPI tiles) and aspireLocationData (table).
         */
        async loadAspireAnalytics() {
            try {
                const { start, end } = this._getAspireDateRange()

                if (!start || !end) {
                    console.warn('Aspire: missing date range — skipping fetch.')
                    return
                }

                const params = new URLSearchParams({
                    start_date: start,
                    end_date: end,
                    range: this.aspireRange,
                })

                const url = `${process.env.VUE_APP_VORTEX_PORTAL_WS}/bots/${ASPIRE_BOT_ID}/get-aspire-stats?${params.toString()}`

                const response = await fetch(url, {
                    method: 'POST',
                    headers: { 'Content-Type': 'application/json' },
                })

                if (!response.ok) {
                    console.error('Aspire Analytics - Fetch failed:', response.statusText)
                    return
                }

                const data = await response.json()

                // Map analytics tiles
                const analytics = data.analytics || {}
                this.aspireStats = {
                    Calls: analytics.Calls ?? 0,
                    Transfers: analytics.Transfers ?? 0,
                    Emails: analytics.Emails ?? 0,
                    SMSs: analytics.SMSs ?? 0,
                }

                // Map location table — each item: { Location, Calls }
                this.aspireLocationData = Array.isArray(data.AspireStats)
                    ? data.AspireStats.map(item => ({
                        Location: item.Location || '—',
                        Calls: item.Calls ?? 0,
                    }))
                    : []

            } catch (error) {
                console.error('Aspire Analytics - Error:', error)
            }
        },

        //  STANDARD (NON-MEDICAL) BOT METHODS 

        /**
         * Loads standard analytics with new date range logic (7d / 14d / Custom).
         * Maps API response to stats cards, daily table, weekly table and bar chart.
         */
        async loadStandardAnalytics() {
            try {
                if (!localStorage.getItem('customerGUID')) {
                    this.$router.push('/partner/dashboard')
                    return
                }

                const botId = this.currentBotId || localStorage.getItem('botID') || ''
                if (!botId) {
                    this.$router.push('/partner/customer')
                    return
                }

                const today = new Date()
                today.setHours(23, 59, 59, 999)
                let start, end

                if (this.standardRange === '7') {
                    end = today.toISOString().split('T')[0]
                    const s = new Date(today)
                    s.setDate(s.getDate() - 7)
                    start = s.toISOString().split('T')[0]
                } else if (this.standardRange === '14') {
                    end = today.toISOString().split('T')[0]
                    const s = new Date(today)
                    s.setDate(s.getDate() - 14)
                    start = s.toISOString().split('T')[0]
                } else if (this.standardRange === 'Custom' && this.standardStartDate && this.standardEndDate) {
                    start = this.standardStartDate
                    const endDate = new Date(this.standardEndDate)
                    endDate.setDate(endDate.getDate() - 1)
                    end = endDate.toISOString().split('T')[0]
                }

                const url = `${process.env.VUE_APP_VORTEX_PORTAL_WS}/bots/${botId}/analytics`
                const params = new URLSearchParams({
                    range: this.standardRange,
                    start_date: start || '',
                    end_date: end || '',
                })

                const response = await fetch(`${url}?${params.toString()}`, {
                    method: 'POST',
                    headers: { 'Content-Type': 'application/json' },
                })

                if (!response.ok) {
                    console.error('Fetch analytics failed:', response.statusText)
                    return
                }

                const data = await response.json()
                const daily = Array.isArray(data) ? data : (data.daily || [])

                const totalCalls = daily.reduce((sum, d) => sum + (d.Total_Calls || 0), 0)
                const transferSum = daily.reduce(
                    (sum, day) => sum + Math.max((day.Total_Calls || 0) - (day.Handled_Calls || 0), 0),
                    0
                )
                const availabilityLogged = daily.reduce((sum, d) => sum + (d.Fleet_Availability_Logged || 0), 0)

                this.standardStats = {
                    totalCalls,
                    transfers: transferSum,
                    emails: daily.reduce((sum, d) => sum + (d.Total_Emails || 0), 0),
                    smss: this.isPPBot
                        ? this._calculateTransferPercent(totalCalls, transferSum)
                        : daily.reduce((sum, d) => sum + (d.Total_SMSs || 0), 0),
                    propertyDetails: daily.reduce((sum, d) => sum + (d.PP_Get_Property_Details || 0), 0),
                    reaListing: daily.reduce((sum, d) => sum + (d.PP_Get_REA_Listing || 0), 0),
                    sendEmailAction: daily.reduce((sum, d) => sum + (d.PP_Send_Email || 0), 0),
                    availabilityLogged,
                }

                this.standardDailyData = daily.map(item => ({
                    Date: item.Date,
                    Total_Calls: item.Total_Calls || 0,
                    Transfers: Math.max((item.Total_Calls || 0) - (item.Handled_Calls || 0), 0),
                    Emails: item.Total_Emails || 0,
                    SMSs: this.isPPBot
                        ? this._calculateTransferPercent(
                            item.Total_Calls || 0,
                            Math.max((item.Total_Calls || 0) - (item.Handled_Calls || 0), 0)
                        )
                        : item.Total_SMSs || 0,
                    PropertyDetails: item.PP_Get_Property_Details || 0,
                    ReaListing: item.PP_Get_REA_Listing || 0,
                    SendEmailAction: item.PP_Send_Email || 0,
                    AvailabilityLogged: item.Fleet_Availability_Logged || 0,
                }))

                this.standardWeeklyData = this.groupStandardByWeek(this.standardDailyData)

                this.$nextTick(() => {
                    this.renderStandardChart()
                })

            } catch (error) {
                console.error('Error fetching standard analytics:', error)
            }
        },

        groupStandardByWeek(data) {
            const weekMap = {}
            const fmt = d =>
                `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`

            const sorted = [...data].sort((a, b) => new Date(a.Date) - new Date(b.Date))

            sorted.forEach(item => {
                const d = new Date(item.Date)
                const startOfWeek = new Date(d)
                startOfWeek.setDate(d.getDate() - d.getDay())
                startOfWeek.setHours(0, 0, 0, 0)

                const endOfWeek = new Date(startOfWeek)
                endOfWeek.setDate(startOfWeek.getDate() + 6)

                const key = fmt(startOfWeek)

                if (!weekMap[key]) {
                    weekMap[key] = {
                        startOfWeek: new Date(startOfWeek),
                        endOfWeek: new Date(endOfWeek),
                        totalCalls: 0,
                        transfers: 0,
                        emails: 0,
                        smss: 0,
                        propertyDetails: 0,
                        reaListing: 0,
                        sendEmailAction: 0,
                        availabilityLogged: 0,
                    }
                }

                weekMap[key].totalCalls += item.Total_Calls || 0
                weekMap[key].transfers += item.Transfers || 0
                weekMap[key].emails += item.Emails || 0
                weekMap[key].smss += item.SMSs || 0
                weekMap[key].propertyDetails += item.PropertyDetails || 0
                weekMap[key].reaListing += item.ReaListing || 0
                weekMap[key].sendEmailAction += item.SendEmailAction || 0
                weekMap[key].availabilityLogged += item.AvailabilityLogged || 0
            })

            return Object.keys(weekMap)
                .sort((a, b) => new Date(a) - new Date(b))
                .map((key, index) => {
                    const w = weekMap[key]
                    return {
                        week: `Week ${index + 1} (${fmt(w.startOfWeek)} to ${fmt(w.endOfWeek)})`,
                        totalCalls: w.totalCalls,
                        transfers: w.transfers,
                        emails: w.emails,
                        smss: this.isPPBot
                            ? this._calculateTransferPercent(w.totalCalls, w.transfers)
                            : w.smss,
                        propertyDetails: w.propertyDetails,
                        reaListing: w.reaListing,
                        sendEmailAction: w.sendEmailAction,
                        availabilityLogged: w.availabilityLogged,
                    }
                })
        },

        renderStandardChart() {
            if (this.standardChartInstance) this.standardChartInstance.destroy()

            const canvas = this.$refs.standardBarChart
            if (!canvas) return

            const labels = this.standardWeeklyData.map(d =>
                d.week.substring(0, d.week.indexOf('(')).trim().replace(' ', '').toLowerCase()
            )

            const ctx = canvas.getContext('2d')
            const datasets = [
                {
                    label: 'Total Calls',
                    data: this.standardWeeklyData.map(d => d.totalCalls),
                    backgroundColor: '#2196F3',
                },
                {
                    label: this.isPPBot ? 'Transfers to PM' : 'Transfers',
                    data: this.standardWeeklyData.map(d => d.transfers),
                    backgroundColor: '#FF9800',
                },
            ]

            // Only add third dataset for non-property bots
            if (this.isFleetBot) {
                datasets.push({
                    label: 'Availability Logged',
                    data: this.standardWeeklyData.map(d => d.availabilityLogged),
                    backgroundColor: '#4CAF50',
                })
            } else if (!this.isPPBot) {
                datasets.push({
                    label: 'SMSs',
                    data: this.standardWeeklyData.map(d => d.smss),
                    backgroundColor: '#4CAF50',
                })
            }

            this.standardChartInstance = new Chart(ctx, {
                type: 'bar',
                data: {
                    labels,
                    datasets,
                },
                options: {
                    responsive: true,
                    plugins: { legend: { position: 'top' } },
                    scales: {
                        y: { beginAtZero: true, title: { display: true, text: 'Count' } },
                        x: { title: { display: true, text: 'Weeks' } },
                    },
                },
            })
        },

        /**
         * Exports standard bot Daily and Weekly data to Excel.
         */
        downloadStandardSummary() {
            if (!this.standardWeeklyData.length && !this.standardDailyData.length) {
                console.warn('No data available to download.')
                return
            }
            const wb = XLSX.utils.book_new()
            XLSX.utils.book_append_sheet(wb, XLSX.utils.json_to_sheet(this.standardDailyData), 'Daily Summary')
            XLSX.utils.book_append_sheet(wb, XLSX.utils.json_to_sheet(this.standardWeeklyData), 'Weekly Summary')
            XLSX.writeFile(wb, 'standard_summary.xlsx')
        },

        //  MEDICAL BOT METHODS 

        /**
         * Generic API fetcher for medical statistics.
         */
        async fetchBotAnalytics(botId, startDate, endDate, range) {
            const url = `${process.env.VUE_APP_VORTEX_PORTAL_WS}/bots/${botId}/medical-stats?start_date=${startDate}&end_date=${endDate}&range=${range}`
            const response = await fetch(url, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
            })
            if (!response.ok) throw new Error(`API error: ${response.status}`)
            return response.json()
        },

        /**
         * Loads medical bot specific analytics.
         */
        async loadMedicalAnalytics() {
            try {
                const today = new Date()
                today.setHours(23, 59, 59, 999)
                let start, end

                if (this.medicalRange === '7') {
                    end = today.toISOString().split('T')[0]
                    const s = new Date(today)
                    s.setDate(s.getDate() - 7)
                    start = s.toISOString().split('T')[0]
                } else if (this.medicalRange === '14') {
                    end = today.toISOString().split('T')[0]
                    const s = new Date(today)
                    s.setDate(s.getDate() - 14)
                    start = s.toISOString().split('T')[0]
                } else if (this.medicalRange === 'Custom' && this.medicalStartDate && this.medicalEndDate) {
                    start = this.medicalStartDate
                    const endDate = new Date(this.medicalEndDate)
                    endDate.setDate(endDate.getDate() - 1)
                    end = endDate.toISOString().split('T')[0]
                }

                const botId = localStorage.getItem('botID')
                const data = await this.fetchBotAnalytics(botId, start, end, this.medicalRange)

                this.tableData = data.map(item => ({
                    Date: item.Date,
                    Total_Calls: item.Total_Calls,
                    Handled_Calls: item.Handled_Calls,
                    Bookings: item.Bookings,
                    Check_Availability: item.Check_Availability || item.checkAvailability || 0,
                    Cancellations: item.Cancellations || item.cancellations || 0,
                    Reschedules: item.Reschedules || item.reschedules || 0,
                    Confirm_Booking: item.Confirm_Booking || item.confirmBooking || 0,
                }))

                const totalCalls = data.reduce((sum, d) => sum + (d.Total_Calls || 0), 0)
                const handledCalls = data.reduce((sum, d) => sum + (d.Handled_Calls || 0), 0)
                const bookings = data.reduce((sum, d) => sum + (d.Bookings || 0), 0)
                const checkAvailability = data.reduce((sum, d) => sum + (d.Check_Availability || d.checkAvailability || 0), 0)
                const cancellations = data.reduce((sum, d) => sum + (d.Cancellations || d.cancellations || 0), 0)
                const reschedules = data.reduce((sum, d) => sum + (d.Reschedules || d.reschedules || 0), 0)
                const confirmBooking = data.reduce((sum, d) => sum + (d.Confirm_Booking || d.confirmBooking || 0), 0)

                const totalActionsCompleted = bookings + reschedules + cancellations + confirmBooking
                const actionsCompletedRate = totalCalls ? ((totalActionsCompleted / totalCalls) * 100).toFixed(1) : 0

                this.medicalStats = {
                    totalCalls,
                    handledCalls,
                    bookings,
                    checkAvailability,
                    cancellations,
                    reschedules,
                    confirmBooking,
                    bookingRate: totalCalls ? ((bookings / totalCalls) * 100).toFixed(1) : 0,
                    handledRate: totalCalls ? ((handledCalls / totalCalls) * 100).toFixed(1) : 0,
                    reschedulesRate: totalCalls ? ((reschedules / totalCalls) * 100).toFixed(1) : 0,
                    confirmBookingRate: totalCalls ? ((confirmBooking / totalCalls) * 100).toFixed(1) : 0,
                    checkAvailabilityRate: totalCalls ? ((checkAvailability / totalCalls) * 100).toFixed(1) : 0,
                    cancellationsRate: totalCalls ? ((cancellations / totalCalls) * 100).toFixed(1) : 0,
                    actionsCompletedRate,
                }

                this.weeklyData = this.groupByWeek(data)
                this.renderChart()
            } catch (e) {
                console.error('Error loading analytics:', e)
            }
        },

        /**
         * Groups daily medical records into weekly intervals (Sun–Sat).
         */
        groupByWeek(data) {
            const weekMap = {}
            const fmt = d =>
                `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`

            /**
             * Parse a date string as LOCAL midnight, not UTC midnight.
             * "2024-05-01" via new Date() → UTC 00:00 → shifted to May 0 in UTC-X zones.
             * Splitting manually and using new Date(y, m, d) avoids the timezone shift.
             */
            const parseLocalDate = (str) => {
                if (!str) return new Date(NaN)
                // Handle both YYYY-MM-DD and DD/MM/YYYY formats
                if (str.includes('/')) {
                    const [d, m, y] = str.split('/')
                    return new Date(Number(y), Number(m) - 1, Number(d))
                }
                const [y, m, d] = str.split('-')
                return new Date(Number(y), Number(m) - 1, Number(d))
            }

            const sorted = [...data]
                .filter(item => item.Date)
                .sort((a, b) => parseLocalDate(a.Date) - parseLocalDate(b.Date))

            if (sorted.length === 0) {
                console.warn('[BotAnalytics] groupByWeek received empty or date-less data array')
                return []
            }

            sorted.forEach(item => {
                const d = parseLocalDate(item.Date)

                if (isNaN(d.getTime())) {
                    console.warn('[BotAnalytics] groupByWeek: unparseable Date value:', item.Date)
                    return
                }
                const startOfWeek = new Date(d)
                startOfWeek.setDate(d.getDate() - d.getDay())
                startOfWeek.setHours(0, 0, 0, 0)

                const endOfWeek = new Date(startOfWeek)
                endOfWeek.setDate(startOfWeek.getDate() + 6)

                const key = fmt(startOfWeek)

                if (!weekMap[key]) {
                    weekMap[key] = {
                        startOfWeek: new Date(startOfWeek),
                        endOfWeek: new Date(endOfWeek),
                        totalCalls: 0,
                        handledCalls: 0,
                        bookings: 0,
                        checkAvailability: 0,
                        cancellations: 0,
                        reschedules: 0,
                        confirmBooking: 0,
                        sms: 0,
                    }
                }

                weekMap[key].totalCalls += item.Total_Calls || 0
                weekMap[key].handledCalls += item.Handled_Calls || 0
                weekMap[key].bookings += item.Bookings || 0
                weekMap[key].checkAvailability += item.Check_Availability || item.checkAvailability || 0
                weekMap[key].cancellations += item.Cancellations || item.cancellations || 0
                weekMap[key].reschedules += item.Reschedules || item.reschedules || 0
                weekMap[key].confirmBooking += item.Confirm_Booking || item.confirmBooking || 0
                weekMap[key].sms += item.Total_SMSs || 0
            })

            return Object.keys(weekMap)
                .sort((a, b) => new Date(a) - new Date(b))
                .map((key, index) => {
                    const w = weekMap[key]
                    return {
                        week: `Week ${index + 1} (${fmt(w.startOfWeek)} to ${fmt(w.endOfWeek)})`,
                        totalCalls: w.totalCalls,
                        handledCalls: w.handledCalls,
                        bookings: w.bookings,
                        checkAvailability: w.checkAvailability,
                        cancellations: w.cancellations,
                        reschedules: w.reschedules,
                        confirmBooking: w.confirmBooking,
                        sms: w.sms,
                    }
                })
        },

        /**
         * Renders medical bot bar chart: Total vs Handled vs Bookings.
         */
        renderChart() {
            if (this.chartInstance) this.chartInstance.destroy()

            const labels = this.weeklyData.map(d =>
                d.week.substring(0, d.week.indexOf('(')).trim().replace(' ', '').toLowerCase()
            )
            const ctx = this.$refs.barChart.getContext('2d')

            this.chartInstance = new Chart(ctx, {
                type: 'bar',
                data: {
                    labels,
                    datasets: [
                        { label: 'Total Calls', data: this.weeklyData.map(d => d.totalCalls), backgroundColor: '#2196F3' },
                        { label: 'Handled Calls', data: this.weeklyData.map(d => d.handledCalls), backgroundColor: '#FF9800' },
                        { label: 'Bookings', data: this.weeklyData.map(d => d.bookings), backgroundColor: '#4CAF50' },
                    ],
                },
                options: {
                    responsive: true,
                    plugins: { legend: { position: 'top' } },
                    scales: {
                        y: { beginAtZero: true, title: { display: true, text: 'Calls' } },
                        x: { title: { display: true, text: 'Weeks' } },
                    },
                },
            })
        },

        /**
         * Exports medical bot Daily and Weekly data to Excel.
         */
        downloadSummary() {
            if (!this.weeklyData.length && !this.tableData.length) {
                console.warn('No data available to download.')
                return
            }
            const wb = XLSX.utils.book_new()
            XLSX.utils.book_append_sheet(wb, XLSX.utils.json_to_sheet(this.tableData), 'Daily Summary')
            XLSX.utils.book_append_sheet(wb, XLSX.utils.json_to_sheet(this.weeklyData), 'Weekly Summary')
            XLSX.writeFile(wb, 'summary.xlsx')
        },
    },
}
</script>

<style scoped>
.stat-card {
    width: 140px;
    height: 140px;
    border-radius: 12px;
    box-shadow: 0 6px 16px rgba(0, 0, 0, 0.12);
    border: 1px solid rgba(255, 255, 255, 0.15);
    transition: transform 0.3s ease, box-shadow 0.3s ease;
    cursor: pointer;
}

.stat-card:hover {
    transform: translateY(-5px);
    box-shadow: 0 12px 24px rgba(0, 0, 0, 0.18);
}

.section-title {
    font-size: 1.25rem;
    font-weight: 700;
    color: #1a1a2e;
    border-left: 4px solid #1976d2;
    padding-left: 12px;
    letter-spacing: 0.01em;
}

/* ── KPI Cards  */
.kpi-card {
    border-radius: 16px;
    padding: 14px 10px;
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: center;
    text-align: center;
    gap: 4px;
    min-height: 130px;
    box-shadow: 0 4px 20px rgba(0, 0, 0, 0.06);
    border: 1px solid rgba(0, 0, 0, 0.04);
    transition: transform 0.3s ease, box-shadow 0.3s ease;
    cursor: default;
    max-width: 280px;
    margin: 0 auto;
}

.kpi-card:hover {
    transform: translateY(-4px);
    box-shadow: 0 8px 30px rgba(0, 0, 0, 0.12);
}

.kpi-header {
    display: flex;
    align-items: center;
    justify-content: center;
    gap: 8px;
}

.kpi-blue {
    background: #e3f0ff;
}

.kpi-amber {
    background: #fff8e1;
}

.kpi-teal {
    background: #e0f5f1;
}

.kpi-green {
    background: #e8f5e9;
}

.kpi-icon {
    opacity: 0.75;
    margin-bottom: 2px;
}

.kpi-blue .kpi-icon {
    color: #1565c0;
}

.kpi-amber .kpi-icon {
    color: #e65100;
}

.kpi-teal .kpi-icon {
    color: #00695c;
}

.kpi-green .kpi-icon {
    color: #2e7d32;
}

.kpi-value {
    font-size: 1.7rem;
    font-weight: 800;
    line-height: 1.1;
}

.kpi-blue .kpi-value {
    color: #1565c0;
}

.kpi-amber .kpi-value {
    color: #e65100;
}

.kpi-teal .kpi-value {
    color: #00695c;
}

.kpi-green .kpi-value {
    color: #1b5e20;
}

.kpi-label {
    font-size: 0.8rem;
    font-weight: 600;
    text-transform: uppercase;
    letter-spacing: 0.04em;
    opacity: 0.75;
}

.kpi-label-with-tooltip {
    font-size: 0.8rem;
    font-weight: 600;
    text-transform: uppercase;
    letter-spacing: 0.04em;
    opacity: 0.75;
    display: flex;
    align-items: center;
    gap: 6px;
    justify-content: center;
}

.info-icon {
    opacity: 0.5;
    cursor: help;
    transition: opacity 0.2s ease;
    flex-shrink: 0;
}

.info-icon:hover {
    opacity: 1;
}

/*  Detail Cards  */
.new-detail-card {
    border-radius: 14px;
    padding: 20px 14px;
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: center;
    text-align: center;
    gap: 10px;
    min-height: 120px;
    box-shadow: 0 2px 10px rgba(0, 0, 0, 0.05);
    border: 1px solid rgba(0, 0, 0, 0.04);
    transition: transform 0.2s ease, box-shadow 0.2s ease;
    cursor: default;
}

.new-detail-card:hover {
    transform: translateY(-3px);
    box-shadow: 0 6px 18px rgba(0, 0, 0, 0.09);
}

.new-detail-top {
    display: flex;
    align-items: center;
    justify-content: center;
    gap: 8px;
}

.new-detail-label {
    font-size: 0.78rem;
    font-weight: 600;
    text-transform: uppercase;
    letter-spacing: 0.03em;
    opacity: 0.75;
}

.new-detail-value {
    font-size: 1.8rem;
    font-weight: 800;
    line-height: 1;
    padding-left: 2px;
    display: flex;
    align-items: center;
    justify-content: center;
    gap: 8px;
}

.new-detail-rate-badge {
    font-size: 0.7rem;
    font-weight: 700;
    padding: 2px 7px;
    border-radius: 999px;
    background: rgba(0, 0, 0, 0.08);
    color: inherit;
    opacity: 0.75;
    letter-spacing: 0.03em;
    vertical-align: middle;
    line-height: 1.6;
}

.new-detail-card.detail-green .new-detail-label,
.new-detail-card.detail-green .detail-icon {
    color: #2e7d32;
}

.new-detail-card.detail-green .new-detail-value {
    color: #1b5e20;
}

.new-detail-card.detail-teal .new-detail-label,
.new-detail-card.detail-teal .detail-icon {
    color: #00695c;
}

.new-detail-card.detail-teal .new-detail-value {
    color: #004d40;
}

.new-detail-card.detail-orange .new-detail-label,
.new-detail-card.detail-orange .detail-icon {
    color: #e65100;
}

.new-detail-card.detail-orange .new-detail-value {
    color: #bf360c;
}

.new-detail-card.detail-yellow .new-detail-label,
.new-detail-card.detail-yellow .detail-icon {
    color: #f9a825;
}

.new-detail-card.detail-yellow .new-detail-value {
    color: #f57f17;
}

.new-detail-card.detail-red .new-detail-label,
.new-detail-card.detail-red .detail-icon {
    color: #c62828;
}

.new-detail-card.detail-red .new-detail-value {
    color: #b71c1c;
}

.detail-green {
    background: #e8f5e9;
}

.detail-teal {
    background: #e0f5f1;
}

.detail-orange {
    background: #fff3e0;
}

.detail-yellow {
    background: #fffde7;
}

.detail-red {
    background: #ffebee;
}

/*  Analytics Layout  */
.analytics-container {
    padding: 0;
    width: 100%;
}

.analytics-wrapper {
    gap: 24px;
    display: flex;
    flex-direction: row;
    flex-wrap: nowrap;
    align-items: stretch;
    width: 100%;
}

/*  Chart  */
.chart-wrapper {
    position: relative;
    height: 450px;
    display: flex;
    align-items: center;
    margin-bottom: 8px;
}

.chart-note {
    font-size: 0.75rem;
    color: rgba(0, 0, 0, 0.65);
    margin-top: 8px;
    font-style: italic;
    text-align: center;
}

/*  Data Table Header  */
.v-data-table :deep(thead th) {
    background-color: #f5f5f5;
    font-weight: 600;
    color: #333;
}

/*  AI Supervisor Cards  */
.ai-cards-grid {
    display: grid;
    grid-template-columns: repeat(4, 1fr);
    gap: 16px;
}

.ai-analytics-card {
    border-radius: 16px;
    overflow: hidden;
    border: 1px solid rgba(0, 0, 0, 0.08);
    box-shadow: 0 4px 15px rgba(0, 0, 0, 0.05);
    transition: all 0.3s cubic-bezier(0.25, 0.8, 0.25, 1);
}

.ai-analytics-card:hover {
    box-shadow: 0 10px 25px rgba(0, 0, 0, 0.1);
    transform: translateY(-3px);
}

.ai-card-header {
    padding: 12px 10px;
    display: flex;
    flex-direction: column;
    gap: 3px;
}

.ai-card-title {
    font-size: 0.8rem;
    font-weight: 700;
    text-transform: uppercase;
    opacity: 0.95;
    color: #ffffff;
}

.ai-card-subtitle {
    font-size: 0.7rem;
    opacity: 0.9;
    letter-spacing: 0.02em;
    color: rgba(255, 255, 255, 0.9);
}

.ai-card-body {
    padding: 0;
}

table.ai-table {
    width: 100%;
    border-collapse: collapse;
    table-layout: fixed;
}

table.ai-table thead tr {
    background: #f7f7f9;
}

table.ai-table thead th {
    position: sticky;
    top: 0;
    background: #ffffff;
    z-index: 2;
    box-shadow: 0 2px 6px rgba(0, 0, 0, 0.03);
}

.ai-th {
    padding: 8px 4px;
    font-size: 0.65rem;
    font-weight: 600;
    letter-spacing: 0.05em;
    text-transform: uppercase;
    border-bottom: 2px solid rgba(0, 0, 0, 0.05);
    color: #666;
    text-align: left;
}

.ai-th:first-child {
    width: 50%;
}

.ai-th:nth-child(2) {
    width: 25%;
}

.ai-th-center {
    text-align: center;
}

.ai-row {
    border-bottom: 1px solid rgba(0, 0, 0, 0.06);
    transition: background 0.12s;
}

.ai-row:last-child {
    border-bottom: none;
}

.ai-row:nth-child(even) {
    background: #fafafa;
}

.ai-row:hover {
    background: linear-gradient(90deg, rgba(250, 250, 255, 1), rgba(245, 248, 255, 1));
    transform: translateY(-1px);
}

.ai-td {
    padding: 8px 4px;
    font-size: 0.75rem;
    color: #333;
    vertical-align: middle;
}

.ai-td-center {
    text-align: center;
}

.ai-dash {
    color: #bbb;
}

.ai-num-pill {
    display: inline-block;
    padding: 4px 6px;
    border-radius: 999px;
    font-weight: 700;
    background: rgba(0, 0, 0, 0.04);
    border: 1px solid rgba(0, 0, 0, 0.06);
    box-shadow: 0 1px 3px rgba(0, 0, 0, 0.04);
    font-size: 0.75rem;
}

.ai-pct-wrap {
    display: flex;
    align-items: center;
    gap: 10px;
    justify-content: flex-end;
}

.ai-pct-bar {
    flex: 0 0 50px;
    height: 8px;
    border-radius: 8px;
    background: rgba(0, 0, 0, 0.06);
    overflow: hidden;
}

.ai-pct-text {
    min-width: 36px;
    text-align: right;
    font-weight: 700;
    font-size: 0.7rem;
}

@media (max-width: 800px) {
    .ai-pct-bar {
        flex: 0 0 90px;
    }

    .ai-pct-text {
        min-width: 28px;
        font-size: 0.72rem;
    }

    .ai-card-body {
        max-height: 320px;
    }

    .analytics-wrapper {
        flex-wrap: wrap;
    }
}
</style>